import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Progress } from '../ui/progress';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { Alert, AlertDescription } from '../ui/alert';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import {
  RadialBarChart,
  RadialBar,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  LineChart,
  Line
} from 'recharts';
import {
  Shield,
  AlertTriangle,
  CheckCircle,
  XCircle,
  Zap,
  Eye,
  TrendingDown,
  TrendingUp,
  Activity,
  BarChart3,
  Settings,
  Calculator,
  Target
} from 'lucide-react';
import { getAITradingService } from '../../services/AITradingService';
import { RiskScore, Transaction } from '../../types/ai-trading';

interface RiskProfile {
  overall: number;
  tolerance: 'conservative' | 'moderate' | 'aggressive';
  maxDrawdown: number;
  positionSizing: number;
  diversification: number;
  stopLossStrategy: string;
}

interface PortfolioRisk {
  var95: number; // Value at Risk 95%
  var99: number; // Value at Risk 99%
  expectedShortfall: number;
  sharpeRatio: number;
  maxDrawdown: number;
  volatility: number;
  concentration: number;
}

interface RiskMetric {
  name: string;
  value: number;
  status: 'low' | 'medium' | 'high' | 'critical';
  description: string;
  recommendation: string;
}

export function RiskAssessmentAI() {
  const [riskProfile, setRiskProfile] = useState<RiskProfile>({
    overall: 65,
    tolerance: 'moderate',
    maxDrawdown: 15,
    positionSizing: 5,
    diversification: 78,
    stopLossStrategy: 'trailing'
  });
  
  const [portfolioRisk, setPortfolioRisk] = useState<PortfolioRisk>({
    var95: 12500,
    var99: 18750,
    expectedShortfall: 22000,
    sharpeRatio: 1.45,
    maxDrawdown: 18.5,
    volatility: 24.8,
    concentration: 42
  });

  const [transactionRisk, setTransactionRisk] = useState<RiskScore | null>(null);
  const [riskMetrics, setRiskMetrics] = useState<RiskMetric[]>([]);
  const [loading, setLoading] = useState(false);
  const [simulatedTransaction, setSimulatedTransaction] = useState<Partial<Transaction>>({
    token: 'ETH',
    value: 1000,
    gasPrice: 50,
    gasLimit: 21000,
    chain: 'ethereum',
    type: 'swap'
  });

  useEffect(() => {
    generateRiskMetrics();
  }, []);

  const generateRiskMetrics = () => {
    const metrics: RiskMetric[] = [
      {
        name: 'Market Risk',
        value: 68,
        status: 'medium',
        description: 'Exposure to overall market volatility and correlation',
        recommendation: 'Consider adding uncorrelated assets to reduce market beta'
      },
      {
        name: 'Liquidity Risk',
        value: 32,
        status: 'low',
        description: 'Risk of not being able to exit positions quickly',
        recommendation: 'Current liquidity levels are adequate for position sizes'
      },
      {
        name: 'Concentration Risk',
        value: 78,
        status: 'high',
        description: 'Over-concentration in specific assets or sectors',
        recommendation: 'Reduce position sizes in top 3 holdings to improve diversification'
      },
      {
        name: 'Smart Contract Risk',
        value: 45,
        status: 'medium',
        description: 'Risk from smart contract vulnerabilities and exploits',
        recommendation: 'Only use audited protocols and limit exposure to experimental DeFi'
      },
      {
        name: 'Regulatory Risk',
        value: 25,
        status: 'low',
        description: 'Risk from changing regulatory environment',
        recommendation: 'Current portfolio has minimal regulatory exposure'
      },
      {
        name: 'Operational Risk',
        value: 38,
        status: 'low',
        description: 'Risk from technical failures, hacks, and operational issues',
        recommendation: 'Implement additional security measures and backup strategies'
      }
    ];
    
    setRiskMetrics(metrics);
  };

  const assessTransactionRisk = async () => {
    if (!simulatedTransaction.token) return;
    
    try {
      setLoading(true);
      
      const transaction: Transaction = {
        hash: '',
        from: '0x...',
        to: '0x...',
        value: simulatedTransaction.value || 0,
        token: simulatedTransaction.token,
        gasPrice: simulatedTransaction.gasPrice || 0,
        gasLimit: simulatedTransaction.gasLimit || 0,
        chain: simulatedTransaction.chain || 'ethereum',
        type: simulatedTransaction.type || 'swap'
      };

      const risk = await getAITradingService().assessRisk(transaction);
      setTransactionRisk(risk);
    } catch (error) {
      console.error('Error assessing transaction risk:', error);
    } finally {
      setLoading(false);
    }
  };

  const getRiskColor = (risk: number) => {
    if (risk < 30) return '#22c55e'; // green
    if (risk < 60) return '#eab308'; // yellow
    if (risk < 80) return '#f97316'; // orange
    return '#ef4444'; // red
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'low': return 'text-green-500';
      case 'medium': return 'text-yellow-500';
      case 'high': return 'text-orange-500';
      case 'critical': return 'text-red-500';
      default: return 'text-gray-500';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'low': return <CheckCircle className="h-4 w-4 text-green-500" />;
      case 'medium': return <AlertTriangle className="h-4 w-4 text-yellow-500" />;
      case 'high': return <AlertTriangle className="h-4 w-4 text-orange-500" />;
      case 'critical': return <XCircle className="h-4 w-4 text-red-500" />;
      default: return <Eye className="h-4 w-4 text-gray-500" />;
    }
  };

  const riskDistributionData = [
    { name: 'Low Risk', value: 35, fill: '#22c55e' },
    { name: 'Medium Risk', value: 45, fill: '#eab308' },
    { name: 'High Risk', value: 20, fill: '#ef4444' }
  ];

  const riskTrendData = Array.from({ length: 30 }, (_, i) => ({
    date: new Date(Date.now() - (29 - i) * 24 * 60 * 60 * 1000).toLocaleDateString(),
    portfolio: Math.random() * 30 + 40,
    market: Math.random() * 25 + 35,
    var95: Math.random() * 20000 + 10000
  }));

  const radialData = [
    { name: 'Portfolio Risk', value: riskProfile.overall, fill: getRiskColor(riskProfile.overall) }
  ];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-3">
          <div className="p-2 rounded-lg bg-orange-100 dark:bg-orange-900">
            <Shield className="h-6 w-6 text-orange-600 dark:text-orange-400" />
          </div>
          <div>
            <h1>AI Risk Assessment</h1>
            <p className="text-muted-foreground">
              Comprehensive risk analysis with machine learning insights
            </p>
          </div>
        </div>

        <div className="flex items-center space-x-2">
          <Badge 
            variant={
              riskProfile.overall < 40 ? 'default' :
              riskProfile.overall < 70 ? 'secondary' : 'destructive'
            }
          >
            Risk Level: {riskProfile.overall < 40 ? 'Low' : 
                       riskProfile.overall < 70 ? 'Medium' : 'High'}
          </Badge>
        </div>
      </div>

      {/* Risk Overview */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Portfolio Risk</p>
                <p className="text-2xl font-bold">{riskProfile.overall}/100</p>
                <p className="text-xs text-muted-foreground">Overall Score</p>
              </div>
              <Shield className="h-8 w-8 text-orange-500" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">VaR (95%)</p>
                <p className="text-2xl font-bold text-red-500">
                  ${portfolioRisk.var95.toLocaleString()}
                </p>
                <p className="text-xs text-muted-foreground">Daily Risk</p>
              </div>
              <TrendingDown className="h-8 w-8 text-red-500" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Sharpe Ratio</p>
                <p className="text-2xl font-bold text-green-500">
                  {portfolioRisk.sharpeRatio}
                </p>
                <p className="text-xs text-muted-foreground">Risk-Adjusted Return</p>
              </div>
              <Target className="h-8 w-8 text-green-500" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Max Drawdown</p>
                <p className="text-2xl font-bold text-yellow-500">
                  {portfolioRisk.maxDrawdown}%
                </p>
                <p className="text-xs text-muted-foreground">Historical Worst</p>
              </div>
              <BarChart3 className="h-8 w-8 text-yellow-500" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Main Content */}
      <Tabs defaultValue="overview" className="space-y-4">
        <TabsList className="grid w-full grid-cols-4">
          <TabsTrigger value="overview">Risk Overview</TabsTrigger>
          <TabsTrigger value="metrics">Risk Metrics</TabsTrigger>
          <TabsTrigger value="transaction">Transaction Risk</TabsTrigger>
          <TabsTrigger value="portfolio">Portfolio Analysis</TabsTrigger>
        </TabsList>

        <TabsContent value="overview" className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <Card>
              <CardHeader>
                <CardTitle>Portfolio Risk Score</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex items-center justify-center">
                  <ResponsiveContainer width="100%" height={200}>
                    <RadialBarChart 
                      innerRadius="60%" 
                      outerRadius="90%" 
                      data={radialData}
                      startAngle={90}
                      endAngle={-270}
                    >
                      <RadialBar 
                        dataKey="value" 
                        cornerRadius={10}
                        fill={getRiskColor(riskProfile.overall)}
                      />
                      <text 
                        x="50%" 
                        y="50%" 
                        textAnchor="middle" 
                        dominantBaseline="middle" 
                        className="text-2xl font-bold fill-current"
                      >
                        {riskProfile.overall}
                      </text>
                    </RadialBarChart>
                  </ResponsiveContainer>
                </div>
                
                <div className="text-center mt-4">
                  <p className="text-sm text-muted-foreground">
                    Risk Tolerance: {riskProfile.tolerance.charAt(0).toUpperCase() + 
                                    riskProfile.tolerance.slice(1)}
                  </p>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Risk Distribution</CardTitle>
              </CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={200}>
                  <PieChart>
                    <Pie
                      data={riskDistributionData}
                      cx="50%"
                      cy="50%"
                      innerRadius={40}
                      outerRadius={80}
                      dataKey="value"
                    >
                      {riskDistributionData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.fill} />
                      ))}
                    </Pie>
                    <Tooltip />
                  </PieChart>
                </ResponsiveContainer>
                
                <div className="flex justify-center space-x-4 mt-4 text-sm">
                  <div className="flex items-center space-x-2">
                    <div className="w-3 h-3 bg-green-500 rounded"></div>
                    <span>Low Risk</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <div className="w-3 h-3 bg-yellow-500 rounded"></div>
                    <span>Medium</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <div className="w-3 h-3 bg-red-500 rounded"></div>
                    <span>High Risk</span>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          <Card>
            <CardHeader>
              <CardTitle>Risk Trend Analysis</CardTitle>
            </CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={300}>
                <LineChart data={riskTrendData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="date" />
                  <YAxis />
                  <Tooltip />
                  <Line 
                    type="monotone" 
                    dataKey="portfolio" 
                    stroke="#8884d8" 
                    name="Portfolio Risk"
                  />
                  <Line 
                    type="monotone" 
                    dataKey="market" 
                    stroke="#82ca9d" 
                    name="Market Risk"
                  />
                </LineChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="metrics" className="space-y-4">
          <div className="grid grid-cols-1 gap-4">
            {riskMetrics.map((metric, index) => (
              <Card key={index} className="hover:shadow-md transition-shadow">
                <CardContent className="p-6">
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex items-center space-x-3">
                      {getStatusIcon(metric.status)}
                      <div>
                        <h3 className="font-semibold">{metric.name}</h3>
                        <p className="text-sm text-muted-foreground">
                          {metric.description}
                        </p>
                      </div>
                    </div>
                    
                    <div className="text-right">
                      <div className={`text-2xl font-bold ${getStatusColor(metric.status)}`}>
                        {metric.value}
                      </div>
                      <p className="text-sm text-muted-foreground">Risk Score</p>
                    </div>
                  </div>

                  <div className="mb-4">
                    <Progress 
                      value={metric.value} 
                      className="h-2"
                      style={{
                        '--progress-background': getRiskColor(metric.value)
                      } as React.CSSProperties}
                    />
                  </div>

                  <Alert>
                    <AlertTriangle className="h-4 w-4" />
                    <AlertDescription>
                      <strong>Recommendation:</strong> {metric.recommendation}
                    </AlertDescription>
                  </Alert>
                </CardContent>
              </Card>
            ))}
          </div>
        </TabsContent>

        <TabsContent value="transaction" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Transaction Risk Analyzer</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="token">Token</Label>
                  <Input
                    id="token"
                    value={simulatedTransaction.token}
                    onChange={(e) => setSimulatedTransaction(prev => ({ 
                      ...prev, 
                      token: e.target.value 
                    }))}
                    placeholder="ETH, BTC, etc."
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="value">Value (USD)</Label>
                  <Input
                    id="value"
                    type="number"
                    value={simulatedTransaction.value}
                    onChange={(e) => setSimulatedTransaction(prev => ({ 
                      ...prev, 
                      value: parseFloat(e.target.value) 
                    }))}
                    placeholder="1000"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="chain">Chain</Label>
                  <select
                    id="chain"
                    value={simulatedTransaction.chain}
                    onChange={(e) => setSimulatedTransaction(prev => ({ 
                      ...prev, 
                      chain: e.target.value 
                    }))}
                    className="w-full px-3 py-2 border rounded-md bg-background"
                  >
                    <option value="ethereum">Ethereum</option>
                    <option value="bsc">BSC</option>
                    <option value="polygon">Polygon</option>
                  </select>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="type">Transaction Type</Label>
                  <select
                    id="type"
                    value={simulatedTransaction.type}
                    onChange={(e) => setSimulatedTransaction(prev => ({ 
                      ...prev, 
                      type: e.target.value as any 
                    }))}
                    className="w-full px-3 py-2 border rounded-md bg-background"
                  >
                    <option value="swap">Swap</option>
                    <option value="transfer">Transfer</option>
                    <option value="stake">Stake</option>
                    <option value="unstake">Unstake</option>
                  </select>
                </div>
              </div>

              <Button 
                onClick={assessTransactionRisk}
                disabled={loading}
                className="w-full"
              >
                {loading ? (
                  <span className="animate-spin mr-2">⟳</span>
                ) : (
                  <Calculator className="h-4 w-4 mr-2" />
                )}
                Analyze Transaction Risk
              </Button>

              {transactionRisk && (
                <div className="mt-6 space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <Card>
                      <CardHeader>
                        <CardTitle>Overall Risk Score</CardTitle>
                      </CardHeader>
                      <CardContent>
                        <div className="text-center">
                          <div className={`text-4xl font-bold ${
                            transactionRisk.overall < 30 ? 'text-green-500' :
                            transactionRisk.overall < 70 ? 'text-yellow-500' : 'text-red-500'
                          }`}>
                            {transactionRisk.overall.toFixed(0)}
                          </div>
                          <p className="text-sm text-muted-foreground mt-2">
                            Recommendation: {transactionRisk.recommendation}
                          </p>
                        </div>
                      </CardContent>
                    </Card>

                    <Card>
                      <CardHeader>
                        <CardTitle>Risk Components</CardTitle>
                      </CardHeader>
                      <CardContent>
                        <div className="space-y-3">
                          {Object.entries(transactionRisk.components).map(([key, value]) => (
                            <div key={key} className="flex items-center justify-between">
                              <span className="text-sm capitalize">
                                {key.replace('_', ' ')}
                              </span>
                              <div className="flex items-center space-x-2">
                                <span className="text-sm font-semibold">{value.toFixed(0)}</span>
                                <Progress value={value} className="w-16" />
                              </div>
                            </div>
                          ))}
                        </div>
                      </CardContent>
                    </Card>
                  </div>

                  {transactionRisk.flags.length > 0 && (
                    <Alert>
                      <AlertTriangle className="h-4 w-4" />
                      <AlertDescription>
                        <strong>Risk Flags:</strong>
                        <ul className="mt-2 list-disc list-inside">
                          {transactionRisk.flags.map((flag, index) => (
                            <li key={index}>{flag}</li>
                          ))}
                        </ul>
                      </AlertDescription>
                    </Alert>
                  )}

                  <Card>
                    <CardHeader>
                      <CardTitle>Risk Analysis</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <p className="text-sm">{transactionRisk.reasoning}</p>
                    </CardContent>
                  </Card>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="portfolio" className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <Card>
              <CardHeader>
                <CardTitle>Value at Risk (VaR)</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <span>VaR 95% (Daily)</span>
                    <span className="font-semibold text-red-500">
                      ${portfolioRisk.var95.toLocaleString()}
                    </span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span>VaR 99% (Daily)</span>
                    <span className="font-semibold text-red-600">
                      ${portfolioRisk.var99.toLocaleString()}
                    </span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span>Expected Shortfall</span>
                    <span className="font-semibold text-red-700">
                      ${portfolioRisk.expectedShortfall.toLocaleString()}
                    </span>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Portfolio Metrics</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <span>Volatility (Annualized)</span>
                    <span className="font-semibold">
                      {portfolioRisk.volatility}%
                    </span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span>Concentration Risk</span>
                    <span className={`font-semibold ${
                      portfolioRisk.concentration > 50 ? 'text-red-500' : 'text-green-500'
                    }`}>
                      {portfolioRisk.concentration}%
                    </span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span>Diversification Score</span>
                    <span className="font-semibold text-green-500">
                      {riskProfile.diversification}%
                    </span>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          <Card>
            <CardHeader>
              <CardTitle>Historical VaR Trend</CardTitle>
            </CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={300}>
                <LineChart data={riskTrendData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="date" />
                  <YAxis />
                  <Tooltip 
                    formatter={(value) => [`$${(value as number).toLocaleString()}`, 'VaR 95%']}
                  />
                  <Line 
                    type="monotone" 
                    dataKey="var95" 
                    stroke="#ef4444" 
                    strokeWidth={2}
                  />
                </LineChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Risk Management Recommendations</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <Alert>
                  <Shield className="h-4 w-4" />
                  <AlertDescription>
                    <strong>Position Sizing:</strong> Current position sizing of {riskProfile.positionSizing}% 
                    per trade is appropriate for your risk tolerance.
                  </AlertDescription>
                </Alert>

                <Alert>
                  <AlertTriangle className="h-4 w-4" />
                  <AlertDescription>
                    <strong>Diversification:</strong> Consider reducing concentration in top holdings 
                    to improve portfolio resilience.
                  </AlertDescription>
                </Alert>

                <Alert>
                  <CheckCircle className="h-4 w-4" />
                  <AlertDescription>
                    <strong>Stop Loss:</strong> Current {riskProfile.stopLossStrategy} stop loss strategy 
                    is well-suited for current market conditions.
                  </AlertDescription>
                </Alert>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}